import { Component, ElementRef, HostBinding, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule, FormControl  } from '@angular/forms';
import { ActivatedRoute, Params, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, combineLatest, EMPTY, finalize, mergeMap, Observable, of, Subject, Subscription, switchMap, take, takeUntil, tap } from 'rxjs';
import dayjs from 'dayjs';
import relativeTime from "dayjs/plugin/relativeTime"; 
dayjs.extend(relativeTime)

import { ChatService } from '../chat.service';
import { AuthService } from '../../auth.service';
import { WebsocketService } from '../websocket.service';
import { AppService } from '../../app.service';
import { LayoutService } from '../../layout.service';
import { EncountersService } from '../../new-encounters/encounters.service';
import { HomeService } from '../../home/home.service';
import { PhotoService } from '../../photo/photo.service';

import { UserTinyComponent } from '../user-tiny/user-tiny.component';

import { ErrorReturn } from '../../shared/interfaces/errorReturn';
import { Message } from '../../shared/interfaces/message';
import { User } from '../../shared/interfaces/user';
import { UserTiny } from '../../shared/interfaces/user-tiny';
import { LoaderComponent } from '../../shared/loader/loader.component';
import { Talk } from '../../shared/interfaces/talk';
import {dataURLtoFileConverter} from '../../shared/tools/dataUrlToFileConverter';

@Component({
  selector: 'app-chat-conversation',
  standalone: true,
  imports: [UserTinyComponent, RouterModule, CommonModule, ReactiveFormsModule, LoaderComponent ],
  templateUrl: './chat-conversation.component.html',
  styleUrl: './chat-conversation.component.scss',
})
export class ChatConversationComponent implements OnInit, OnDestroy {
  @HostBinding('class.fullscreen') openConservation: boolean = false;
  @ViewChild('messagesHolder') messagesHolder!: ElementRef;
  @ViewChild('textareaHolder') textareaHolder!: ElementRef;
  @ViewChild('photoFileInput') photoFileInput!: ElementRef;

  userData = new BehaviorSubject<UserTiny | null | undefined>(undefined)
  talkingId!: number
  messageFormGroup!: UntypedFormGroup;
  messages$ = new BehaviorSubject<Message[]>([])
  isFocused = false;
  loaded = false;
  loadingConversation = true;
  user: User | null = null;
  isNew = false;
  sendingMessage = false;
  silentTalking = false;
  silentTalkingReceived = false;
  roomToken: string | null = null;
  tinyLoader = false;
  loadingMore = false;
  noMoreToLoad = false;
  lastMessageId: number | null = null;
  messagePhoto: string | null = null;
  hasMessageSet = false;
  notSubscribe = false;
  subscribeParams: Params | null = null;
  textareaHeight = 48;
  currentUrl = window.location.href;
  //LP = environment.LP;
  relatedUser!: User | null
  private readonly _destroyed = new Subject<void>()
  me!: User | null
  messageSubscription!: Subscription
  isDesktop = this.layoutService.isDesktop
  talk!: Talk
  messageNotSent = false
  photoFile: File | null = null

  constructor(
    private chatService: ChatService,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private formBuilder: UntypedFormBuilder,
    private websocket: WebsocketService,
    private appService: AppService,
    private layoutService: LayoutService,
    private encountersService: EncountersService,
    private homeService: HomeService,
    private photoService: PhotoService
  ) {
    this.messageFormGroup = this.formBuilder.group({
      text: new FormControl<string | null>(null),
      photo: new FormControl<File | null>(null)
    })
  }

  ngOnInit() {
    console.log('ngoninit')
    this.authService.authMe$.pipe(takeUntil(this._destroyed)).subscribe(res => this.me = res)
    
    this.chatService.relatedUserId.pipe(
      take(1),
      mergeMap(id => {
        return combineLatest([this.authService.getProfile(id!), this.encountersService.getRelationshipStatus(id!)]).pipe(
          switchMap(([user, status]) => {
            this.relatedUser = user
            if(status === 'notfriend') this.silentTalking = true
            return this.setUserData(user)
          }),
          tap(userTiny => {
            this.userData.next(userTiny)
          })
        )
      }),
    ).subscribe(() => console.log('subscribe'))

    this.chatService.conversationClosed.pipe(takeUntil(this._destroyed)).subscribe(roomToken => {
      if(!roomToken) return
      if(this.messageSubscription) this.messageSubscription.unsubscribe()
      this.chatService.messageSent.next(null)
    })

    this.activatedRoute.params.subscribe((param) => {
      const id: string = param['id']

      if(id.indexOf('new') > -1) { // new conversation case
        this.isNew = true
        const userId = parseInt(id.split('-')[1])
        this.setNewConversation(userId)

        this.authService.getProfile(userId).pipe(takeUntil(this._destroyed)).subscribe(res => {
          this.relatedUser = res
          
          //if(res.talking_id) this.talkingId = res.talking_id
        })
        
        if(!this.isDesktop()) {
          this.chatService.toggleSidenav.next(true)
          this.chatService.componentDisplayed.next('conversation')
          this.homeService.pageDisplayed.next('mobile-conversation')        
        }
      } else { // existing conversation case
        this.messages$.next([])

        this.talkingId = Number(id)

        if(this.talkingId) {
          this.loadingConversation = true
          this.messages$.next([])
          this.lastMessageId = null
        }

        this.chatService.conversationToShow = this.talkingId
        this.getAllMessages(this.talkingId, this.lastMessageId)
      }

      this.scrollToBottom(0)

      this.activatedRoute.queryParams.subscribe((params) => {
        if (params['success'] === '1' || params['success'] === 1) {
          this.subscribeParams = params

          if (params['alreadysub'] && (params['alreadysub'] === 1 || params['alreadysub'] === '1')) {
            const err: ErrorReturn = {
              code: 500,
              error_code: 404,
              level: 'already subscribe',
              message: '',
            }

            this.appService.setPopin(err);
            this.notSubscribe = true
          } else {
            this.chatService.setSubscribe(params['subscription_id']).pipe(takeUntil(this._destroyed)).subscribe((res) => {console.log(res)})
          }
        } else if (params['success'] === '0' || params['success'] === 0) {
          const err: ErrorReturn = {
            code: 500,
            error_code: 404,
            level: 'subscribe notfound',
            message: '',
          }

          this.appService.setPopin(err);
        }
      });
    });
  }
  
  setNewConversation(id: number) {
    console.log('setNewConversation')
    this.chatService.conversationToShow = Number(id) // conversation ID

    // TODO : Terminer le refacto de ces appels
    this.authService.getProfile(Number(id)).pipe(
      mergeMap(res =>{
        this.user = res

        if(res.relationship === 'notfriend' || res.relationship === 'waiting' || res.relationship === 'pending'){
          this.handleNoRelation(res.id)
          
          return this.setUserData(res)
        } else {

          return this.handleRelation(res) 
        }
      }),
      finalize(() => console.log('finalize'))
    ).subscribe()
  }

  handleNoRelation(id: number){
    this.silentTalking = true

    //console.log(this.silentTalking)
    this.isNew = true
    this.messages$.next([])
    this.loadingConversation = false
    this.chatService.setConversationToShow(Number(id))
  }

  handleRelation(res: User){
    if (!res.talking_id) {
        return combineLatest([this.setUserData(res), this.chatService.createConversation(res.id)]).pipe(
          switchMap(([, talkId]) => {
            this.talkingId = talkId
            this.getAllMessages(talkId, null)

            return EMPTY
          })
        )
      } else {
        this.talkingId = res.talking_id
        this.getAllMessages(this.talkingId, null)

        return EMPTY
        //setTimeout(() => console.log('set new conversation', this.messages), 2048)
      }
  }

  scrollToBottom(toScroll: number) {
    if (this.messagesHolder && this.messagesHolder.nativeElement.scrollHeight !== toScroll) {
      this.messagesHolder.nativeElement.scrollTop = this.messagesHolder.nativeElement.scrollHeight - toScroll;
    } else {
      setTimeout(() => {
        this.scrollToBottom(toScroll);
      }, 512);
    }
  }

  getAllMessages(talkId: number, lastMessageId: number | null, loadingmore: boolean = false) {
    console.log('getAllMessages')
    const toScroll = this.messagesHolder ? this.messagesHolder.nativeElement.scrollHeight : 0

    if (this.messagesHolder && this.messagesHolder.nativeElement.scrollTop < 10) {
      this.messagesHolder.nativeElement.scrollTop = 40
    }

    this.chatService.relatedUserId.pipe(
      takeUntil(this._destroyed),
      switchMap(id => {
        if(!id) return of(null)
        return this.authService.getProfile(id)
      }),
      switchMap(user => {
        if(!user) return of(null)
        return this.setUserData(user)
      })
    ).subscribe(userTiny => {
      this.userData.next(userTiny)
    })

    this.chatService.getMessages(talkId, lastMessageId).pipe(takeUntil(this._destroyed)).subscribe((res) => {
      this.loaded = true

      if (res.messages.length === 0 && this.messages$.value.length === 0) {
        this.isNew = true
      } else {
        this.isNew = false
        const tArray = res.messages.concat(this.messages$.value)
        this.messages$.next(tArray)
        this.lastMessageId = this.messages$.value[0].id

        this.messages$.value.map(
          (m: Message) => {
            m.text = decodeURIComponent(m.text)

            const time = dayjs().to(dayjs(m.created_at))
            let dateStr = ''
            let timeStr = ''

            if(time.includes('hour') || time.includes('minute') || time.includes('second')){
              if(time.includes('hour')){
                // get the number of hours to define if time was today or yesterday
                const timeDetails = time.split(" ");
                const hours = parseInt(timeDetails[0])
                const now = dayjs()

                if(hours < 24 && hours > now.hour()){
                  dateStr = 'Yesterday'
                } else {
                  dateStr = 'Today'
                }
              } else {
                dateStr = 'Today'
              }
            } else if(time === 'a day ago') {
              dateStr = 'Yesterday'
            } else {
              dateStr = dayjs(m.created_at).format('YYYY-MM-DD')
            }

            timeStr = dayjs(m.created_at).format('hh:mm A')

            m.date_expression = dateStr
            m.time_expression = timeStr

            if (m.photos) {
              m.photos.error = false
              m.photos.loaded = false
            }
          }
        )
      }

      this.loadingConversation = false

      if (!loadingmore) {
        this.user = res.user
        this.roomToken = res.room
        this.chatService.roomToken = res.room
        this.websocketConnection(talkId, toScroll)
        this.silentTalkingReceived = res.silent && res.messages[0] && res.messages[0].user_id === this.user.id
      } else {
        this.tinyLoader = false
        this.noMoreToLoad = res.messages.length < 10
        this.loadingMore = false
      }

      this.scrollToBottom(toScroll)
    })
  }

  websocketConnection(
    talkingId: number,
    toScroll: number,
    msg?: { userId: number; value: string; photo: string }
  ) {
    console.log('websocketConnection')
    if(!this.roomToken) return
    this.websocket.connect(Number(talkingId), msg, this.roomToken, null)
    
    this.messageSubscription?.unsubscribe()
    this.messageSubscription = this.websocket.message.subscribe((m) => {
      const message = JSON.parse(m.data)

      if(message.code === 500){
        this.sendingMessage = false
        this.messageNotSent = true
      } else if (message.code !== 500) {
        if(message.photo?.url){
          this.photoService.sendPhotoToSignedUrl(message.photo.signed_url, this.photoFile!, 'jpeg').subscribe(() => {
            const updatedMessages = this.updateMessagesArray(m, message)
            this.messages$.next(updatedMessages)
          })
        } else {
          const updatedMessages = this.updateMessagesArray(m, message)
          this.messages$.next(updatedMessages)
        }
        
        // send info to chat service that a message has been sent. A new talk is then created and added to the conversation list
        if(this.chatService.messageSent.value?.talkingId !== this.talkingId || this.chatService.messageSent.value.message !== decodeURIComponent(message.message.text)) {
          this.chatService.messageSent.next({talkingId: this.talkingId, message: decodeURIComponent(message.message.text)})
          this.chatService.relatedUser.next(this.userData.value!)
        }
      } else {
        if (message.error_code === 32) this.nonSubscriptionPopup()
      }

      if (this.sendingMessage) {
        this.sendingMessage = false
        this.removeMessagePhoto()
        this.isNew = false
        this.messageFormGroup.patchValue({ text: null });
        this.textareaHolder.nativeElement.innerHTML = ''
        this.textareaHeight = this.textareaHolder.nativeElement.offsetHeight
        this.textareaHolder.nativeElement.focus()
      }

      this.checkIfMessage()

      setTimeout(() => {
        this.scrollToBottom(toScroll)
      }, 512)
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateMessagesArray(m: any, message: any){
    const timestamp = message.timestamp * 1000

    const tempMessages = this.messages$.value
    const time = dayjs().to(dayjs(m.created_at))
    let dateStr = ''
    let timeStr = ''

    if(time.includes('hour') || time.includes('minute') || time.includes('second')){
      if(time.includes('hour')){
        // get the number of hours to define if time was today or yesterday
        const timeDetails = time.split(" ");
        const hours = parseInt(timeDetails[0])
        const now = dayjs()

        if(hours < 24 && hours > now.hour()){
          dateStr = 'Yesterday'
        } else {
          dateStr = 'Today'
        }
      } else {
        dateStr = 'Today'
      }
    } else if(time === 'a day ago') {
      dateStr = 'Yesterday'
    } else {
      dateStr = dayjs(m.created_at).format('YYYY-MM-DD')
    }

    timeStr = dayjs(m.created_at).format('hh:mm A')
    
    tempMessages.push({
      created_at: timestamp,
      id: message.message.id,
      photos: message.photo,
      text: decodeURIComponent(message.message.text),
      user_id: message.user_id, // sender's id
      date_expression: dateStr,
      time_expression: timeStr
      // today: this.checkIfToday(timestamp),
      // yesterday: this.checkIfYesterday(timestamp),
    })

    return tempMessages
  }

  nonSubscriptionPopup() {
    this.notSubscribe = true
  }

  setUserData(user: User): Observable<UserTiny> {
    if(this.chatService.talk){
      return of({
        user: {
          firstname: user.firstname,
          type: 'friend',
          id: user.id,
          sex: user.sex,
          photo: user.main_photo ? user.main_photo.url : null,
        },
        message: this.chatService.talk.last_message.content,
        photos: false,
        time: this.chatService.talk.last_message.created_at,
        talking: user.talking_id,
      })
    }

    return of({
      user: {
        firstname: user.firstname,
        type: 'friend',
        id: user.id,
        sex: user.sex,
        photo: user.main_photo ? user.main_photo.url : null,
      },
      message: null,
      photos: false,
      time: null,
      talking: null,
    })
  }

  focused(value: boolean) {
    this.isFocused = value;
  }

  typing(e: Event) {
    console.log(e)
    this.checkIfMessage()
  }

  checkIfMessage() {
    this.hasMessageSet = this.messagePhoto || (this.messageFormGroup.value.text && this.messageFormGroup.value.text !== '');
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  onPhotoInputChange($event: any) {
    const reader = new FileReader()
    const [file]: File[] = $event.target.files
    reader.readAsDataURL(file)

    reader.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d') as CanvasRenderingContext2D
      const imageResult = reader.result
      const img = new Image()
      img.src = imageResult as string

      img.onload = () => {
        let width = 0
        let height = 0
        const landscape = img.width > img.height

        if (landscape) {
          width = Math.min(500, img.width)
          height = (width / img.width) * img.height
        } else {
          height = Math.min(500, img.height)
          width = (height / img.height) * img.width
        }

        canvas.width = width
        canvas.height = height
        ctx.drawImage(img, 0, 0, width, height)

        this.messagePhoto = canvas.toDataURL('image/jpeg', 0.5)
        this.hasMessageSet = true
      };
    };
  }

  sendMessageCheck() {
    const message = this.messageFormGroup.value.text ? encodeURIComponent(this.messageFormGroup.value.text) : ''
    this.sendingMessage = true

    console.log('sendMessageCheck')

    if (this.silentTalking) {
      console.log('silent talking')
      // create an autovalidated relationship
      if(!this.relatedUser?.id) return
      this.chatService.askForSilentTalking(this.relatedUser?.id).pipe(takeUntil(this._destroyed)).subscribe(res => {
        this.roomToken = res.data.room_talking.toString()
        this.chatService.roomToken = res.data.room_talking.toString()
        this.talkingId = res.data.talking_id
        this.silentTalking = false
        this.websocketConnection(this.talkingId, 0, {
          userId: Number(this.relatedUser?.id),
          value: message,
          photo: this.messagePhoto || '',
        })
        this.chatService.talkingId.next(res.data.talking_id)

        this.sendMessage(message);
      })
    } else if (this.silentTalkingReceived) {
      console.log('silent talking received')
      if(!this.talkingId) return
      this.chatService.changeSilentTalking(this.talkingId, '0').pipe(takeUntil(this._destroyed)).subscribe(() => {
        this.silentTalkingReceived = false;
        this.sendMessage(message);
      });
    } else {
      console.log('else')
      this.sendMessage(message);
    }
  }

  onSubmit(){
    console.log('on submit')
    this.sendingMessage = true
    this.sendMessageCheck()
  }

  sendMessage(message: string) {
    console.log('sendMessage: ', message)

    if (this.messagePhoto) {
      const file = dataURLtoFileConverter(this.messagePhoto, '')
      this.photoFile = file
      this.websocket.sendPhotoMessage(this.talkingId!, this.messagePhoto, message)
    } else {
      if(!this.userData.value?.user.id && !this.chatService.relatedUser.value?.user.id) return

      if(this.userData.value?.user.id){
        console.log('userData')
        this.websocket.sendMessage(this.talkingId!.toString(), this.userData.value.user.id, message)
      } else if(this.chatService.relatedUser.value?.user.id) {
        console.log('chatService relatedUser')
        this.websocket.sendMessage(this.talkingId!.toString(), this.chatService.relatedUser.value?.user.id, message)
      } else {
        console.log('relatedUser')
        this.websocket.sendMessage(this.talkingId!.toString(), this.relatedUser!.id, message)
      }
    }
  }

  removeMessagePhoto() {
    this.messagePhoto = null
    this.photoFileInput.nativeElement.value = null
    this.checkIfMessage()
  }

  onTalkScroll($event: Event) {
    const container = $event.target as HTMLElement
    const bottom = container.scrollTop < 200

    if (bottom && !this.loadingMore && !this.noMoreToLoad) {
      this.tinyLoader = true
      this.loadingMore = true

      if(!this.talkingId || !this.lastMessageId) return

      this.getAllMessages(this.talkingId, this.lastMessageId, true)
    }
  }

  redirect() {
    //window.open(this.LP + this.currentUrl, '_self');
  }

  acceptSilentTalking() {
    if(!this.talkingId) return

    this.chatService.changeSilentTalking(this.talkingId, '0').pipe(takeUntil(this._destroyed)).subscribe(() => {
      this.silentTalkingReceived = false

      // hide the silent talking notification in the talk list for the accepted talk
      this.chatService.hideSilentTalkingNotification.next(this.talkingId)
    });
  }

  denySilentTalking() {
    this.chatService.changeSilentTalking(this.talkingId, '0').pipe(takeUntil(this._destroyed)).subscribe(() => {
      if(!this.talkingId) return

      this.chatService.deleteTalking(this.talkingId).pipe(takeUntil(this._destroyed)).subscribe(() => {
        this.chatService.toggleSidenav.next(this.isDesktop())
        this.chatService.setConversationToShow(null)

        // remove denied talk from the talk list
        this.chatService.hideDeniedSilentTalking.next(this.talkingId)
      });
    });
  }

  ngOnDestroy(){
    this.chatService.setConversationToShow(null);
    this.chatService.conversationClosed.next(this.talkingId.toString())
    this.messageSubscription?.unsubscribe();
    this.websocket.close(this.roomToken!)
    this._destroyed.next();
    this._destroyed.complete();    
  }

  /* 
   checkIfToday(time: number) {
    const today = new Date().toDateString();
    const date = new Date(time).toDateString();
    return date === today;
  }

  checkIfYesterday(time: number) {
    const today = new Date();
    const date = new Date(time).toDateString();
    const yesterday = new Date(today.setDate(today.getDate() - 1)).toDateString();
    return date === yesterday;
  }
  */
}
